import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, InputNumber, Popconfirm, Progress, Select, Space, Table, Tag, Timeline, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudDownloadOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { get, post } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './DataPullPage.css';

type PullType = 'sleep-videos' | 'sleep-music';

interface DataPullPageProps {
  type: PullType;
}

interface PullResultItem {
  id?: number;
  title: string;
  url?: string;
  source_url?: string;
  created?: boolean;
  playable?: boolean;
  error?: string;
}

interface PullTask {
  task_id: string;
  resource_type: string;
  platform: string;
  channel?: string;
  requested: number;
  status: 'running' | 'success' | 'failed' | 'cancelled';
  stage: string;
  done: number;
  total: number;
  imported: number;
  skipped: number;
  failed: number;
  items: PullResultItem[];
  message: string;
  cancel_requested: boolean;
  created_at: string;
  updated_at: string;
  finished_at: string;
}

const config = {
  'sleep-videos': {
    title: '助眠视频',
    endpoint: API_ENDPOINTS.storage.dataPullSleepVideos,
    resourceType: 'sleep_video',
    target: '资源管理 / 助眠视频',
    platforms: [
      { label: 'B站助眠区', value: 'bilibili' },
    ],
    defaultCount: 5,
    maxCount: 500,
    description: 'B站助眠区：分页检索助眠/白噪音/疗愈中文视频，成功和视频级失败记录会在下次直接过滤，不占用期望新增名额。下载 360P 单文件并转存默认云存储（仅拉取时长 30 分钟内的视频，内容版权归原 UP 主，商用前请确认授权）。',
  },
  'sleep-music': {
    title: '助眠音乐',
    endpoint: API_ENDPOINTS.storage.dataPullSleepMusic,
    resourceType: 'sleep_music',
    target: '资源管理 / 背景音乐库',
    platforms: [{ label: 'Wikimedia 公开音频', value: 'wikimedia' }],
    defaultCount: 5,
    maxCount: 500,
    description: '分页扫描 Wikimedia 最新公开授权音频，重复资源不占新增条数，持续向后查找雨声、溪流、冥想钢琴等新内容，并上传到默认云存储。',
  },
};

const channelOptions = [
  { label: '随机分配（推荐）', value: 'random' },
  { label: '起床铃声', value: 'wake' },
  { label: '晚安哄睡', value: 'sleep' },
  { label: '男声陪伴', value: 'male' },
  { label: '女声陪伴', value: 'female' },
];

const statusText: Record<PullTask['status'], string> = {
  running: '正在拉取',
  success: '拉取完成',
  failed: '拉取失败',
  cancelled: '已终止',
};

const DataPullPage: React.FC<DataPullPageProps> = ({ type }) => {
  const page = config[type];
  const [form] = Form.useForm();
  const [task, setTask] = useState<PullTask | null>(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const pollTimerRef = useRef<number | null>(null);

  const initialValues = useMemo(
    () => ({
      platform: page.platforms[0].value,
      count: page.defaultCount,
      channel: type === 'sleep-videos' ? 'random' : 'sleep',
    }),
    [page.defaultCount, page.platforms, type],
  );

  const running = task?.status === 'running';

  const stopPolling = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const fetchTask = useCallback(async (taskId: string) => {
    try {
      const data = await get<{ task: PullTask | null }>(API_ENDPOINTS.storage.dataPullTask(taskId));
      if (data.task) setTask(data.task);
    } catch {
      // 轮询单次失败忽略,下一轮继续
    }
  }, []);

  // 进入页面(或切换类型)时恢复最近一次任务:跳走再回来能接着看进度
  useEffect(() => {
    stopPolling();
    form.setFieldsValue(initialValues);
    setTask(null);
    let alive = true;
    (async () => {
      try {
        const data = await get<{ task: PullTask | null }>(API_ENDPOINTS.storage.dataPullTaskLatest(page.resourceType));
        if (alive && data.task) setTask(data.task);
      } catch {
        // 无历史任务或查询失败,保持空白
      }
    })();
    return () => {
      alive = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // 任务运行中则每 2 秒轮询进度
  useEffect(() => {
    stopPolling();
    if (running && task?.task_id) {
      const id = task.task_id;
      pollTimerRef.current = window.setInterval(() => fetchTask(id), 2000);
    }
    return stopPolling;
  }, [running, task?.task_id, fetchTask]);

  const submit = async () => {
    const values = await form.validateFields();
    setStarting(true);
    try {
      const data = await post<PullTask>(page.endpoint, values);
      setTask(data);
      message.success('拉取任务已发起，可离开页面，回来会自动恢复进度');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '发起拉取失败');
    } finally {
      setStarting(false);
    }
  };

  const cancel = async () => {
    if (!task?.task_id) return;
    setCancelling(true);
    try {
      const data = await post<{ task: PullTask }>(API_ENDPOINTS.storage.dataPullTaskCancel(task.task_id));
      if (data.task) setTask(data.task);
      message.info('终止请求已提交，正在停止当前下载…');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '终止失败');
    } finally {
      setCancelling(false);
    }
  };

  const clearResult = () => {
    if (running) return;
    setTask(null);
    form.setFieldsValue(initialValues);
  };

  const percent = useMemo(() => {
    if (!task) return 0;
    if (task.status !== 'running') return 100;
    if (task.total > 0) return Math.min(99, Math.round((task.done / task.total) * 100));
    return 5;
  }, [task]);

  const progressStatus = useMemo(() => {
    if (!task) return 'normal' as const;
    if (task.status === 'running') return 'active' as const;
    if (task.status === 'success' && task.failed === 0) return 'success' as const;
    if (task.status === 'failed') return 'exception' as const;
    return 'normal' as const;
  }, [task]);

  const timelineItems = useMemo(() => {
    if (!task) return [];
    const items: { color: string; children: React.ReactNode }[] = [
      {
        color: 'green',
        children: `平台：${page.platforms.find((item) => item.value === task.platform)?.label || task.platform}，频道：${channelOptions.find((item) => item.value === (task.channel || 'sleep'))?.label || '晚安哄睡'}，计划新增 ${task.requested} 条。`,
      },
    ];
    (task.items || []).forEach((item) => {
      const action = item.error
        ? `失败（已记录，下次过滤）：${item.error}`
        : item.created
          ? '新增入库'
          : type === 'sleep-music'
            ? '历史重复，继续向后扫描'
            : '已存在，跳过重复写入';
      const playable = item.playable === undefined ? '' : item.playable ? '，可播放' : '，平台未公开可播放直链';
      items.push({
        color: item.error ? 'red' : 'green',
        children: `${item.title || '未知资源'}：${action}${playable}`,
      });
    });
    if (task.status === 'running') {
      items.push({ color: 'blue', children: task.stage || '正在拉取…' });
    } else {
      items.push({
        color: task.status === 'failed' ? 'red' : task.status === 'cancelled' ? 'gray' : 'green',
        children: task.message || statusText[task.status],
      });
    }
    return items;
  }, [task, page.platforms, type]);

  const columns: ColumnsType<PullResultItem> = [
    {
      title: '名称',
      dataIndex: 'title',
      render: (value, record) => (
        <div className="pull-result-title">
          <strong>{value || '未知资源'}</strong>
          {record.error && <span>{record.error}</span>}
        </div>
      ),
    },
    {
      title: '状态',
      width: 110,
      render: (_, record) => {
        if (record.error) return <Tag color="red">失败</Tag>;
        return record.created ? <Tag color="green">新增</Tag> : <Tag>已存在</Tag>;
      },
    },
    {
      title: '地址',
      width: 220,
      render: (_, record) => {
        const url = record.url || record.source_url;
        return url ? <a href={url} target="_blank" rel="noreferrer">打开</a> : '-';
      },
    },
  ];

  return (
    <div className="data-pull-page">
      <h1 className="page-title">数据拉取 - {page.title}</h1>
      <Alert className="data-pull-alert" type="info" showIcon message={page.description} description={`拉取在后台执行:发起后可以离开本页或关闭浏览器,回来会自动恢复进度。完成后数据进入「${page.target}」,文件存入当前默认云存储。`} />

      <Card bordered={false}>
        <Form form={form} layout="inline" initialValues={initialValues}>
          <Form.Item label="平台" name="platform" rules={[{ required: true, message: '请选择平台' }]}>
            <Select className="pull-platform-select" options={page.platforms} disabled={running} />
          </Form.Item>
          <Form.Item label="期望新增条数" name="count" rules={[{ required: true, message: '请输入条数' }]}>
            <InputNumber min={1} max={page.maxCount} precision={0} disabled={running} />
          </Form.Item>
          {type === 'sleep-videos' && (
            <Form.Item label="首页频道" name="channel" rules={[{ required: true, message: '请选择频道' }]}>
              <Select options={channelOptions} disabled={running} style={{ width: 170 }} />
            </Form.Item>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" icon={<CloudDownloadOutlined />} loading={starting} disabled={running} onClick={submit}>
                {running ? '拉取进行中' : '开始拉取'}
              </Button>
              {running && (
                <Popconfirm title="确定终止本次拉取？已入库的内容会保留。" onConfirm={cancel} okText="终止" cancelText="再等等">
                  <Button danger icon={<StopOutlined />} loading={cancelling || task?.cancel_requested}>
                    {task?.cancel_requested ? '正在停止…' : '终止'}
                  </Button>
                </Popconfirm>
              )}
              <Button icon={<ReloadOutlined />} onClick={clearResult} disabled={running}>清空结果</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {task && (
        <Card className="pull-progress-card" bordered={false}>
          <div className="pull-progress-head">
            <strong>{statusText[task.status]}</strong>
            <span>
              {task.status === 'running'
                ? `${task.stage || '正在拉取…'}${task.total > 0 ? `（已处理 ${task.done}/${task.total}）` : ''}`
                : task.message || '本次拉取已结束。'}
            </span>
          </div>
          <Progress percent={percent} status={progressStatus} />
          <Timeline className="pull-progress-timeline" items={timelineItems} />
        </Card>
      )}

      {task && task.status !== 'running' && (
        <Card className="pull-result-card" bordered={false}>
          <div className="pull-result-summary">
            <Tag color="blue">请求 {task.requested}</Tag>
            <Tag color="green">新增 {task.imported}</Tag>
            <Tag>{type === 'sleep-music' ? '历史重复' : '历史记录过滤'} {task.skipped}</Tag>
            <Tag color={task.failed ? 'red' : 'default'}>失败 {task.failed}</Tag>
            {task.status === 'cancelled' && <Tag color="orange">已终止</Tag>}
          </div>
          <Table rowKey={(record, index) => `${record.title}-${index}`} columns={columns} dataSource={task.items || []} pagination={{ pageSize: 8 }} />
        </Card>
      )}
    </div>
  );
};

export default DataPullPage;
